// {{WORKTREE_ROOT}}/picosponsorconsole/app/utils/failureText.ts
// PLAN2 WP10 / PLAN3 C-2 — client-safe. Splits an ActionFailure's messageId at the first "." into section and id, calls
// t(section, id, details as args), and falls back to t("errors", "internal") when that returns "" (C-133). A detail
// whose value is a locale id (`section.id` shape, e.g. the API's `settings.feature-roles-grants`) is resolved through t
// first. IntlMessageFormat throws when a placeholder has no value, so a throw — or a "{" surviving in the output —
// returns errors.internal and console.warns once per messageId; it never crashes the page into the error boundary.
import type { T } from "./locale";
import type { ActionFailure } from "./actionResult";

const LOCALE_ID = /^[a-z][a-z0-9]*\.[a-z0-9]+(?:-[a-z0-9]+)*$/;
const warned = new Set<string>();

function resolveArgs(t: T, details: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(details)) {
    out[k] = v;
    if (typeof v !== "string" || !LOCALE_ID.test(v)) continue;
    const dot = v.indexOf(".");
    try {
      const text = t(v.slice(0, dot), v.slice(dot + 1));
      if (text) out[k] = text;
    } catch {
      // PLAN-BEST-EFFORT: a detail that merely looks like a locale id is shown as the API sent it.
    }
  }
  return out;
}

export function failureText(t: T, error: ActionFailure["error"]): string {
  const dot = error.messageId.indexOf(".");
  const section = dot >= 0 ? error.messageId.slice(0, dot) : "errors";
  const id = dot >= 0 ? error.messageId.slice(dot + 1) : "internal";
  const details = error.details && typeof error.details === "object" ? (error.details as Record<string, unknown>) : undefined;
  let text = "";
  try {
    text = t(section, id, details ? resolveArgs(t, details) : undefined);
    if (text.includes("{")) text = "";
  } catch {
    text = "";
  }
  if (!text) {
    if (!warned.has(error.messageId)) {
      warned.add(error.messageId);
      console.warn(`failureText: ${error.messageId} could not be formatted from its details — showing errors.internal`);
    }
    return t("errors", "internal");
  }
  return text;
}

/** Test-only. */
export function __resetFailureTextWarnings() { warned.clear(); }
